/**
 * Loading a level package: the load-time checks, and the id indexes the
 * reducer looks things up through.
 *
 * A package arrives as JSON with everything addressed by id — rules name
 * outcomes, outcomes name channels and particles, highlights name particles.
 * Walking arrays for each of those in the solver's inner loop would dominate
 * the search, so `loadPack` builds the indexes once and every later lookup is a
 * record read.
 *
 * `loadPack` is also where a malformed package is stopped. The validator CLI is
 * the primary gate, but the engine refuses to run a package that would produce
 * nonsense — notably `max`/`min` on a nominal axis, which has no declared order
 * to compare against and would silently never fire.
 */
import { checkVocabulary, } from '@managing-up/schema';
/** Thrown by `createGame` when a package cannot be run as authored. */
export class PackLoadError extends Error {
    issues;
    constructor(message, issues) {
        super(message);
        this.name = 'PackLoadError';
        this.issues = issues;
    }
}
/** The key a harvested span is recorded under in `GameState.harvested`. */
export const harvestKey = (messageId, highlightId) => `${messageId}:${highlightId}`;
const indexBy = (items, key) => {
    const index = {};
    for (const item of items)
        index[key(item)] = item;
    return index;
};
/**
 * Structural invariants of the channel and message model that the engine relies
 * on but JSON Schema cannot express.
 *
 * Both are load-time refusals rather than warnings because both would fail
 * silently and late. A DM with three members renders as a private channel with
 * the wrong name and no way to say whose it is; a thread nested two deep is a
 * conversation the message list can group but the interface has no second tier
 * to draw, so the reply would simply vanish.
 */
function checkChannelModel(pack) {
    const issues = [];
    pack.channels.forEach((channel, index) => {
        if (channel.access === 'dm' && channel.members.length !== 2) {
            issues.push({
                code: 'dm-member-count',
                pointer: `/channels/${String(index)}/members`,
                message: `DM channel '${channel.id}' must have exactly two members, has ${String(channel.members.length)}.`,
            });
        }
    });
    const replies = pack.backlog
        .filter((message) => message.threadParentId !== undefined)
        .map((message) => message.id);
    pack.backlog.forEach((message, index) => {
        const parentId = message.threadParentId;
        if (parentId === undefined)
            return;
        const pointer = `/backlog/${String(index)}/threadParentId`;
        if (parentId === message.id) {
            issues.push({
                code: 'thread-depth',
                pointer,
                message: `Message '${message.id}' is its own thread parent.`,
            });
            return;
        }
        if (replies.includes(parentId)) {
            issues.push({
                code: 'thread-depth',
                pointer,
                message: `Threads are single level: '${message.id}' replies to '${parentId}', which is itself a reply.`,
            });
        }
    });
    return issues;
}
/**
 * Indexes a package and refuses the ones the engine cannot honour.
 *
 * Throws `PackLoadError` listing every issue found rather than the first, so a
 * failing package can be fixed in one pass.
 */
export function loadPack(pack) {
    const issues = [
        ...checkVocabulary(pack),
        ...checkChannelModel(pack),
    ];
    if (issues.length > 0) {
        const summary = issues.map((issue) => `${issue.pointer}: ${issue.message}`).join('\n  ');
        throw new PackLoadError(`Package '${pack.manifest.id}' cannot be loaded; ${String(issues.length)} vocabulary issue(s):\n  ${summary}`, issues);
    }
    const harvestSpans = {};
    for (const message of pack.backlog) {
        for (const highlight of message.highlights ?? []) {
            harvestSpans[harvestKey(message.id, highlight.id)] = { messageId: message.id, highlight };
        }
    }
    // Spans authored on an outcome's posts become harvestable once that post
    // lands. Indexing them here keeps the harvest lookup uniform.
    for (const outcome of Object.values(pack.outcomes)) {
        for (const post of outcome.post ?? []) {
            if (post.id === undefined)
                continue;
            for (const highlight of post.highlights ?? []) {
                harvestSpans[harvestKey(post.id, highlight.id)] = { messageId: post.id, highlight };
            }
        }
    }
    return {
        pack,
        nodes: indexBy(pack.cast, (node) => node.id),
        channels: indexBy(pack.channels, (channel) => channel.id),
        particles: indexBy(pack.particles, (particle) => particle.id),
        outcomes: { ...pack.outcomes },
        puppets: indexBy(pack.puppets, (puppet) => puppet.nodeId),
        backlog: indexBy(pack.backlog, (message) => message.id),
        harvestSpans,
    };
}
//# sourceMappingURL=pack.js.map